import type { getAuthAccount, getAuthUser } from '@/shared/auth/selectors';
import PhoneUtil from '@/shared/util/phoneutil';

export interface UserData {
  Email: string;
  FirstName: string;
  LastName: string;
  Company: string;
  NumberOfEmployees: number;
  Phone: string;
  Country: string;
}

/**
 * Formats a phone number to match Rippling's expected format.
 * Converts international format "+1 612 730 2758" to "+1 6127302758"
 * (country code with space, then digits without spaces).
 *
 * @param phoneNumber - The phone number to format
 * @param countryCode - Optional country code for proper formatting
 * @returns Formatted phone number string or empty string if invalid
 */
export function formatPhoneNumberForRippling(phoneNumber: string | null | undefined, countryCode?: string): string {
  if (!phoneNumber) {
    return '';
  }

  try {
    const formatted = PhoneUtil.formatLocal(phoneNumber, countryCode as any);
    // formatLocal returns international format like "+1 612 730 2758"
    // We need to convert it to "+1 6127302758" (only one space after country code)
    const match = formatted.match(/^(\+\d+)\s(.*)$/);
    if (match) {
      const countryCodePart = match[1];
      const numberPart = match[2].replace(/\s/g, '');
      return `${countryCodePart} ${numberPart}`;
    }
    return formatted;
  } catch (_error) {
    // If parsing fails, return the original number
    return phoneNumber;
  }
}

/**
 * Calculates the employee count based on account data and trial status.
 * For trial accounts, prioritizes ref_employees over user_count.
 * For paid accounts, uses user_count.
 *
 * @param account - The account object from Redux state
 * @param isTrial - Whether the account is in trial mode
 * @returns The employee count
 */
export function getEmployeeCount(account: ReturnType<typeof getAuthAccount> | null, isTrial: boolean): number {
  const trialCount = account?.ref_employees || 0;
  const paidCount = account?.user_count || 0;
  return isTrial ? (trialCount > 0 ? trialCount : paidCount) : paidCount;
}

/**
 * Extracts user and account data into a standardized format for Rippling API payloads.
 *
 * @param user - The user object from Redux state
 * @param account - The account object from Redux state
 * @param isTrial - Whether the account is in trial mode
 * @param country - The full country name (defaults to 'United States' if not provided)
 * @returns UserData object with formatted fields
 */
export function extractUserData(
  user: ReturnType<typeof getAuthUser> | null,
  account: ReturnType<typeof getAuthAccount> | null,
  isTrial: boolean,
  country?: string,
): UserData {
  return {
    Email: user?.email || '',
    FirstName: user?.first_name || '',
    LastName: user?.last_name || '',
    Company: account?.company || '',
    NumberOfEmployees: getEmployeeCount(account, isTrial),
    Phone: formatPhoneNumberForRippling(user?.phone_number, user?.country_code),
    Country: country || 'United States',
  };
}

/**
 * Maps Google Place country code (ISO 2-letter) to Rippling accepted country name.
 * Google Places API returns country as short_name (ISO 2-letter code like "US", "CA", etc.)
 * If no mapping is found or no country is provided, returns null.
 *
 * @param googlePlaceCountryCode - ISO 2-letter country code from Google Place API
 * @returns Mapped country name or null if no match found
 */
export function mapGooglePlaceCountryToRipplingCountry(
  googlePlaceCountryCode: string | null | undefined,
): string | null {
  if (!googlePlaceCountryCode) {
    return null;
  }

  // Google Places API returns ISO 2-letter country codes (short_name)
  // Map these codes to Rippling's full country names
  const isoCodeToRipplingCountry: Record<string, string> = {
    // Major countries
    AD: 'Andorra',
    AE: 'United Arab Emirates',
    AF: 'Afghanistan',
    AG: 'Antigua and Barbuda',
    AI: 'Anguillaå',
    AL: 'Albania',
    AM: 'Armenia',
    AO: 'Angola',
    AQ: 'Antarctica',
    AR: 'Argentina',
    AS: 'American Samoa',
    AT: 'Austria',
    AU: 'Australia',
    AW: 'Aruba',
    AX: 'Åland Islands', // Not in Rippling list
    AZ: 'Azerbaijan',
    BA: 'Bosnia and Herzegovina',
    BB: 'Barbados',
    BD: 'Bangladesh',
    BE: 'Belgium',
    BF: 'Burkina Faso',
    BG: 'Bulgaria',
    BH: 'Bahrain',
    BI: 'Burundi',
    BJ: 'Benin',
    BL: 'Saint Barthelemy',
    BM: 'Bermuda',
    BN: 'Brunei',
    BO: 'Bolivia',
    BQ: 'Bonaire, Sint Eustatius and Saba', // Not in Rippling list
    BR: 'Brazil',
    BS: 'Bahamas',
    BT: 'Bhutan',
    BV: 'Bouvet Island', // Not in Rippling list
    BW: 'Botswana',
    BY: 'Belarus',
    BZ: 'Belize',
    CA: 'Canada',
    CC: 'Cocos Islands',
    CD: 'Democratic Republic of the Congo',
    CF: 'Central African Republic',
    CG: 'Republic of the Congo',
    CH: 'Switzerland',
    CI: 'Ivory Coast',
    CK: 'Cook Islands',
    CL: 'Chile',
    CM: 'Cameroon',
    CN: 'China',
    CO: 'Colombia',
    CR: 'Costa Rica',
    CU: 'Cuba',
    CV: 'Cape Verde',
    CW: 'Curacao',
    CX: 'Christmas Island',
    CY: 'Cyprus',
    CZ: 'Czech Republic',
    DE: 'Germany',
    DJ: 'Djibouti',
    DK: 'Denmark',
    DM: 'Dominica',
    DO: 'Dominican Republic',
    DZ: 'Algeria',
    EC: 'Ecuador',
    EE: 'Estonia',
    EG: 'Egypt',
    EH: 'Western Sahara',
    ER: 'Eritrea',
    ES: 'Spain',
    ET: 'Ethiopia',
    FI: 'Finland',
    FJ: 'Fiji',
    FK: 'Falkland Islands',
    FM: 'Micronesia',
    FO: 'Faroe Islands',
    FR: 'France',
    GA: 'Gabon',
    GB: 'United Kingdom',
    GD: 'Grenada',
    GE: 'Georgia',
    GF: 'French Guiana', // Not in Rippling list
    GG: 'Guernsey',
    GH: 'Ghana',
    GI: 'Gibraltar',
    GL: 'Greenland',
    GM: 'Gambia',
    GN: 'Guinea',
    GP: 'Guadeloupe', // Not in Rippling list
    GQ: 'Equatorial Guinea',
    GR: 'Greece',
    GS: 'South Georgia and the South Sandwich Islands', // Not in Rippling list
    GT: 'Guatemala',
    GU: 'Guam',
    GW: 'Guinea-Bissau',
    GY: 'Guyana',
    HK: 'Hong Kong',
    HM: 'Heard Island and McDonald Islands', // Not in Rippling list
    HN: 'Honduras',
    HR: 'Croatia',
    HT: 'Haiti',
    HU: 'Hungary',
    ID: 'Indonesia',
    IE: 'Ireland',
    IL: 'Israel',
    IM: 'Isle of Man',
    IN: 'India',
    IO: 'British Indian Ocean Territory',
    IQ: 'Iraq',
    IR: 'Iran',
    IS: 'Iceland',
    IT: 'Italy',
    JE: 'Jersey',
    JM: 'Jamaica',
    JO: 'Jordan',
    JP: 'Japan',
    KE: 'Kenya',
    KG: 'Kyrgyzstan',
    KH: 'Cambodia',
    KI: 'Kiribati',
    KM: 'Comoros',
    KN: 'Saint Kitts and Nevis',
    KP: 'North Korea',
    KR: 'South Korea',
    KW: 'Kuwait',
    KY: 'Cayman Islands',
    KZ: 'Kazakhstan',
    LA: 'Laos',
    LB: 'Lebanon',
    LC: 'Saint Lucia',
    LI: 'Liechtenstein',
    LK: 'Sri Lanka',
    LR: 'Liberia',
    LS: 'Lesotho',
    LT: 'Lithuania',
    LU: 'Luxembourg',
    LV: 'Latvia',
    LY: 'Libya',
    MA: 'Morocco',
    MC: 'Monaco',
    MD: 'Moldova',
    ME: 'Montenegro',
    MF: 'Saint Martin',
    MG: 'Madagascar',
    MH: 'Marshall Islands',
    MK: 'Macedonia',
    ML: 'Mali',
    MM: 'Myanmar',
    MN: 'Mongolia',
    MO: 'Macau',
    MP: 'Northern Mariana Islands',
    MQ: 'Martinique', // Not in Rippling list
    MR: 'Mauritania',
    MS: 'Montserrat',
    MT: 'Malta',
    MU: 'Mauritius',
    MV: 'Maldives',
    MW: 'Malawi',
    MX: 'Mexico',
    MY: 'Malaysia',
    MZ: 'Mozambique',
    NA: 'Namibia',
    NC: 'New Caledonia',
    NE: 'Niger',
    NF: 'Norfolk Island', // Not in Rippling list
    NG: 'Nigeria',
    NI: 'Nicaragua',
    NL: 'Netherlands',
    NO: 'Norway',
    NP: 'Nepal',
    NR: 'Nauru',
    NU: 'Niue',
    NZ: 'New Zealand',
    OM: 'Oman',
    PA: 'Panama',
    PE: 'Peru',
    PF: 'French Polynesia',
    PG: 'Papua New Guinea',
    PH: 'Philippines',
    PK: 'Pakistan',
    PL: 'Poland',
    PM: 'Saint Pierre and Miquelon',
    PN: 'Pitcairn',
    PR: 'Puerto Rico',
    PS: 'Palestine',
    PT: 'Portugal',
    PW: 'Palau',
    PY: 'Paraguay',
    QA: 'Qatar',
    RE: 'Reunion',
    RO: 'Romania',
    RS: 'Serbia',
    RU: 'Russia',
    RW: 'Rwanda',
    SA: 'Saudi Arabia',
    SB: 'Solomon Islands',
    SC: 'Seychelles',
    SD: 'Sudan',
    SE: 'Sweden',
    SG: 'Singapore',
    SH: 'Saint Helena',
    SI: 'Slovenia',
    SJ: 'Svalbard and Jan Mayen',
    SK: 'Slovakia',
    SL: 'Sierra Leone',
    SM: 'San Marino',
    SN: 'Senegal',
    SO: 'Somalia',
    SR: 'Suriname',
    SS: 'South Sudan',
    ST: 'Sao Tome and Principe',
    SV: 'El Salvador',
    SX: 'Sint Maarten',
    SY: 'Syria',
    SZ: 'Swaziland',
    TC: 'Turks and Caicos Islands',
    TD: 'Chad',
    TF: 'French Southern Territories', // Not in Rippling list
    TG: 'Togo',
    TH: 'Thailand',
    TJ: 'Tajikistan',
    TK: 'Tokelau',
    TL: 'East Timor',
    TM: 'Turkmenistan',
    TN: 'Tunisia',
    TO: 'Tonga',
    TR: 'Turkey',
    TT: 'Trinidad and Tobago',
    TV: 'Tuvalu',
    TW: 'Taiwan',
    TZ: 'Tanzania',
    UA: 'Ukraine',
    UG: 'Uganda',
    UM: 'United States Minor Outlying Islands', // Not in Rippling list
    US: 'United States',
    UY: 'Uruguay',
    UZ: 'Uzbekistan',
    VA: 'Vatican',
    VC: 'Saint Vincent and the Grenadines',
    VE: 'Venezuela',
    VG: 'British Virgin Islands',
    VI: 'U.S. Virgin Islands',
    VN: 'Vietnam',
    VU: 'Vanuatu',
    WF: 'Wallis and Futuna',
    WS: 'Samoa',
    YE: 'Yemen',
    YT: 'Mayotte',
    ZA: 'South Africa',
    ZM: 'Zambia',
    ZW: 'Zimbabwe',
  };

  const upperCaseCode = googlePlaceCountryCode.toUpperCase().trim();
  return isoCodeToRipplingCountry[upperCaseCode] || null;
}
